import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import GameToolbar from '../../../components/GameToolbar/GameToolbar';
import { GAME_COPY, GAME_KEYS } from '../../../data/games';
import { finishGame } from '../../../utils/gameSave';
import {
  candySrc,
  clearMotionFlags,
  collapseWithMotion,
  createBoard,
  findMatches,
  removeMatches,
  swapCandies,
} from './candyEngine';
import './CandyCrushPage.css';

const CRUSH_MS = 280;
const FALL_MS = 360;
const BOARD_SIZE = 8;

function formatTime(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export default function CandyCrushPage() {
  const navigate = useNavigate();
  const user = useSelector((s) => s.auth.user);
  const [state, setState] = useState(() => createBoard(BOARD_SIZE));
  const [timeLeft, setTimeLeft] = useState(120);
  const [selected, setSelected] = useState(null);
  const [pulse, setPulse] = useState(false);
  const [locked, setLocked] = useState(false);
  const [crushing, setCrushing] = useState(() => new Set());
  const [falling, setFalling] = useState(false);
  const doneRef = useRef(false);
  const elapsedRef = useRef(0);
  const scoreRef = useRef(0);
  const dragRef = useRef(null);
  const animatingRef = useRef(false);
  const gridRef = useRef(state.grid);

  useEffect(() => {
    scoreRef.current = state.score;
  }, [state.score]);

  useEffect(() => {
    gridRef.current = state.grid;
  }, [state.grid]);

  const submit = useCallback(async () => {
    if (doneRef.current || !user) return;
    doneRef.current = true;
    setLocked(true);
    const points = scoreRef.current;
    try {
      const result = await finishGame({
        user,
        gameKey: GAME_KEYS.candyCrush,
        score: points,
        elapsedSeconds: elapsedRef.current,
      });
      if (result.alreadyPlayed) {
        navigate('/hub');
      } else {
        navigate(
          `/thank-you?game=${GAME_KEYS.candyCrush}&points=${result.coinsEarned ?? 0}`
        );
      }
    } catch (err) {
      console.error(err);
      doneRef.current = false;
      setLocked(false);
    }
  }, [navigate, user]);

  useEffect(() => {
    const tick = setInterval(() => {
      elapsedRef.current += 1;
      setTimeLeft((t) => {
        if (t <= 1) {
          clearInterval(tick);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, []);

  useEffect(() => {
    if (timeLeft === 0) submit();
  }, [timeLeft, submit]);

  const runResolve = useCallback(async (startGrid, baseScore) => {
    let grid = startGrid;
    let score = baseScore;
    let anyGain = false;

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const matched = findMatches(grid, BOARD_SIZE);
      if (matched.size === 0) break;

      // Crush animation while candies still visible
      setCrushing(new Set(matched));
      await sleep(CRUSH_MS);
      setCrushing(new Set());

      const removed = removeMatches(grid, matched);
      score += removed.crushed;
      anyGain = true;

      const fallen = collapseWithMotion(removed.grid, BOARD_SIZE);
      grid = fallen;
      gridRef.current = grid;
      setState({ grid, score, size: BOARD_SIZE });
      setFalling(true);
      await sleep(FALL_MS);

      grid = clearMotionFlags(grid);
      gridRef.current = grid;
      setState({ grid, score, size: BOARD_SIZE });
      setFalling(false);
    }

    if (anyGain) {
      setPulse(true);
      setTimeout(() => setPulse(false), 450);
    }
    return { grid, score };
  }, []);

  const applySwap = async (r1, c1, r2, c2) => {
    if (locked || doneRef.current || animatingRef.current) return;
    const swapped = swapCandies(
      { grid: gridRef.current, size: BOARD_SIZE },
      r1,
      c1,
      r2,
      c2
    );
    if (!swapped) {
      setSelected(null);
      return;
    }

    animatingRef.current = true;
    setLocked(true);
    setSelected(null);
    gridRef.current = swapped;
    setState((prev) => ({ ...prev, grid: swapped }));

    await sleep(100);
    const next = await runResolve(swapped, scoreRef.current);
    scoreRef.current = next.score;
    animatingRef.current = false;
    if (!doneRef.current) setLocked(false);
  };

  const onCellClick = (r, c) => {
    if (locked || animatingRef.current) return;
    if (!selected) {
      setSelected({ r, c });
      return;
    }
    if (selected.r === r && selected.c === c) {
      setSelected(null);
      return;
    }
    applySwap(selected.r, selected.c, r, c);
  };

  const onPointerDown = (e, r, c) => {
    if (locked || animatingRef.current) return;
    dragRef.current = {
      r,
      c,
      x: e.clientX,
      y: e.clientY,
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPointerUp = (e) => {
    const start = dragRef.current;
    dragRef.current = null;
    if (!start || locked || animatingRef.current) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    if (Math.abs(dx) < 18 && Math.abs(dy) < 18) return;
    let r2 = start.r;
    let c2 = start.c;
    if (Math.abs(dx) > Math.abs(dy)) {
      c2 = start.c + (dx > 0 ? 1 : -1);
    } else {
      r2 = start.r + (dy > 0 ? 1 : -1);
    }
    applySwap(start.r, start.c, r2, c2);
  };

  const copy = GAME_COPY.candy_crush;
  const size = state.size;

  return (
    <div className="candy-page bg-games" style={{ '--page-bg': 'url(/img/games-background.jpg)' }}>
      <GameToolbar rules={copy.rules} />
      <p className="candy-title">{copy.title}</p>
      <div className="candy-stats">
        <div className={`candy-pill ${pulse ? 'pulse' : ''}`}>{state.score} points</div>
        <div className="candy-pill">Time Left: {formatTime(timeLeft)}</div>
      </div>
      <div className="candy-board" style={{ gridTemplateColumns: `repeat(${size}, 1fr)` }}>
        {state.grid.flatMap((row, r) =>
          row.map((candy, c) => {
            const key = `${r},${c}`;
            const isCrush = crushing.has(key);
            const from = candy?.fromRow ?? candy?.row ?? r;
            const dropRows = candy && falling ? candy.row - from : 0;
            return (
              <button
                key={candy?.id ?? `e-${r}-${c}`}
                type="button"
                className={`candy-cell ${
                  selected && selected.r === r && selected.c === c ? 'selected' : ''
                } ${isCrush ? 'crushing' : ''} ${falling && candy && dropRows !== 0 ? 'falling' : ''}`}
                style={
                  falling && candy && dropRows !== 0
                    ? { '--drop-rows': dropRows }
                    : undefined
                }
                disabled={locked}
                onClick={() => onCellClick(r, c)}
                onPointerDown={(e) => onPointerDown(e, r, c)}
                onPointerUp={onPointerUp}
              >
                {candy ? (
                  <img src={candySrc(candy.color)} alt={candy.color} draggable={false} />
                ) : null}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
